import React, { useState } from "react";
import { BASE_URL } from "@/lib/API";
import { DetailsWorkspaceModal, TableActionButton } from "../../../../components/ui";
import { Caption } from "@/components/ui/Typography";
import { Calendar, User, Eye, Phone, Activity, FileText, MessageSquare } from "lucide-react";
import { fmtDate } from "@/lib/formatters";
import { SiteVisitMediaPreviewModal } from "./SiteVisitMediaPreviewModal";

export const ViewSiteVisitModal = ({ isOpen, onClose, lead }) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  if (!lead) return null;

  const siteVisitData = lead.siteVisitData || {};
  const displayName = lead.customerName || `${lead.firstName || ""} ${lead.lastName || ""}`.trim() || "Lead";
  const lastNote = siteVisitData.commonNotes || "No notes yet";

  // Aggregate all photos across measurements with context
  const allMedia = [];
  siteVisitData.measurements?.forEach(meas => {
    if (meas.photos && Array.isArray(meas.photos)) {
      meas.photos.forEach(photo => {
        allMedia.push({
          ...photo,
          fileRaw: photo,
          room: meas.room,
          particulars: meas.particulars
        });
      });
    }
  });

  // Group media by room
  const groupedMedia = allMedia.reduce((acc, item) => {
    const room = item.room || "Other";
    if (!acc[room]) acc[room] = [];
    acc[room].push(item);
    return acc;
  }, {});

  const fields = [
    { icon: User, label: "Verve ID", value: (lead.customId || "").toUpperCase() },
    { icon: Phone, label: "Contact Details", value: lead.phoneNumber || lead.phone || lead.email || "—" },
    { icon: Activity, label: "Lead Status", value: lead.leadStatus?.replace('_', ' ') },
    { icon: Calendar, label: "Updated At", value: fmtDate(siteVisitData.updatedAt || siteVisitData.createdAt || lead.updatedAt || lead.createdAt) },
    { icon: FileText, label: "Media Count", value: `${allMedia.length} Files` },
    { icon: MessageSquare, label: "Site Visit Note", value: lastNote }
  ];

  // Map activities to timeline format for the right pane
  const timeline = lead.activities?.map(a => {
    let text = a.action;
    if (a.note) text += ` - ${a.note}`;
    return text;
  }) || [];

  const handlePreview = (index) => {
    setPreviewIndex(index);
    setIsPreviewOpen(true);
  };

  let globalMediaIdx = 0;

  return (
    <>
      <DetailsWorkspaceModal
        isOpen={isOpen}
        onClose={onClose}
        title="VERVE KITCHENS"
        subtitle="SITE VISIT DETAILS"
        fields={fields}
        timeline={timeline}
        timelineTitle="MEASUREMENTS COLLECTED"
      >
        <div className="flex flex-col gap-8">
          
          {/* Measurements Block */}
          {siteVisitData.rooms?.length > 0 && (
            <div className="flex flex-col">
              <h4 className="text-[11px] font-bold text-slate-400 tracking-wider mb-4 uppercase">
                Measurements & Requirements
              </h4>
              <div className="flex flex-col gap-3">
                {siteVisitData.rooms.map(room => {
                  const roomMeasurements = siteVisitData.measurements?.filter(m => m.room === room) || [];
                  if (roomMeasurements.length === 0) return null;
                  return (
                    <div key={room} className="border border-slate-100 bg-slate-50/50 rounded-xl p-3">
                      <h5 className="text-[10px] font-bold text-slate-500 uppercase mb-2 bg-white inline-block px-2 py-0.5 rounded shadow-sm border border-slate-100">{room} Measurements ({roomMeasurements.length})</h5>
                      <div className="flex flex-col gap-2">
                        {roomMeasurements.map((m, idx) => {
                          const dims = [m.width ? `W: ${m.width}` : '', m.height ? `H: ${m.height}` : '', m.depth ? `D: ${m.depth}` : ''].filter(Boolean).join(' x ');
                          return (
                            <div key={idx} className="bg-white p-2.5 rounded border border-slate-100 text-[11px] text-slate-600 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 shadow-sm hover:shadow-md transition-all">
                              {m.zone && <div className="font-semibold text-slate-700 min-w-[80px]">Zone: {m.zone}</div>}
                              {dims && <div><span className="font-semibold">Dims:</span> {dims}</div>}
                              {m.qty && <div><span className="font-semibold">Qty:</span> {m.qty}</div>}
                              {m.particulars && <div className="flex-1 truncate" title={m.particulars}><span className="font-semibold">Particulars:</span> {m.particulars}</div>}
                              {m.notes && <div className="w-full sm:w-auto mt-1 sm:mt-0 text-slate-500 italic bg-slate-50 px-2 py-0.5 rounded border border-slate-100">Note: {m.notes}</div>}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex flex-col">
            <h4 className="text-[11px] font-bold text-slate-400 tracking-wider mb-4 uppercase">
              Media Files (Classified)
            </h4>

            <div className="w-full h-[180px] min-h-[180px] max-h-[180px]">
            {allMedia.length > 0 ? (
              <div className="flex flex-col pb-4 h-full overflow-y-auto pr-2 custom-scrollbar">
                {Object.entries(groupedMedia).map(([room, photos]) => (
                  <div key={room} className="mb-4">
                    <h5 className="text-[10px] font-bold text-slate-500 uppercase mb-2 bg-slate-100 py-1 px-2 rounded inline-block">{room}</h5>
                    <div className="flex flex-col gap-3">
                      {photos.map((photoObj) => {
                        const currentIndex = globalMediaIdx++;
                        const fileRaw = photoObj.fileRaw;
                        const fileId = typeof fileRaw === 'string' ? fileRaw : (fileRaw.fileId || fileRaw._id);
                        const filename = fileRaw.filename || `SiteVisit_Photo_${currentIndex + 1}.jpg`;
                        const mimetype = fileRaw.mimetype || 'image/jpeg';
                        const size = fileRaw.size || null;
                        const isImage = mimetype.startsWith('image/');
                        const isVideo = mimetype.startsWith('video/');
                        const isPdf = mimetype === 'application/pdf';
                        const fileUrl = `${BASE_URL}/api/verve-leads/site-visit/media/${fileId}`;

                        return (
                          <div
                            key={currentIndex}
                            className="group flex items-center bg-white p-3 rounded-xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all gap-4 w-full"
                          >
                            <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden shrink-0 border border-slate-100 p-1.5">
                              {isImage ? (
                                <img src={fileUrl} alt={filename} className="w-full h-full object-contain" />
                              ) : isVideo ? (
                                <span className="text-lg">🎬</span>
                              ) : isPdf ? (
                                <div className="flex flex-col items-center">
                                  <span className="text-lg">📄</span>
                                </div>
                              ) : (
                                <span className="text-lg">📁</span>
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <p className="text-[12px] font-bold text-slate-700 truncate mb-0.5" title={filename}>
                                {filename}
                              </p>
                              <div className="flex items-center gap-2">
                                <Caption className="text-[10px] text-slate-400 font-medium">
                                  {size ? (size / 1024 / 1024).toFixed(2) : "Unknown"} MB
                                </Caption>
                                <span className="text-slate-300 text-[10px]">•</span>
                                <Caption className="text-[10px] text-slate-400 font-medium truncate max-w-[120px]">
                                  {photoObj.particulars || (filename?.split('.').pop()?.toUpperCase() || 'FILE')}
                                </Caption>
                              </div>
                            </div>

                            <div className="flex items-center gap-1 shrink-0">
                              <TableActionButton
                                title="View Media"
                                icon={Eye}
                                onClick={() => handlePreview(currentIndex)}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full bg-[#f8fafc] rounded-xl border border-slate-200 border-dashed">
                <span className="text-2xl mb-2 text-slate-300">📁</span>
                <span className="text-[11px] font-medium text-slate-500 text-center px-4">No media uploaded yet.</span>
              </div>
            )}
          </div>
        </div>
        </div>
      </DetailsWorkspaceModal>
      
      <SiteVisitMediaPreviewModal 
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        lead={lead}
        mediaList={allMedia.map(m => m.fileRaw)}
        initialMediaIndex={previewIndex}
      />
    </>
  );
};
